/* Settings-UI atoms: the grammar of every settings surface — workspace Settings,
   an agent's Settings, Interfaces, the eval dialogs. `Pane > Group > RowList > Row`:
   a page title, a titled block, a white card in a grey tray, one setting per row. */

import React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/shared/lib/cn";

// Top of each content pane: one title + optional description and action.
export function Pane({ title, desc, action, children }: {
  title?: React.ReactNode;
  desc?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="fade-in-up">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-7">
        <div className="min-w-0">
          <h2 className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">{title}</h2>
          {desc && <p className="m-0 mt-1.5 max-w-[560px] text-[13.5px] leading-[1.5] text-[var(--text-tertiary)]">{desc}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      {children}
    </div>
  );
}

// A titled block of a pane: a 15px heading (and its one action) over its content.
export function Group({ title, desc, action, children, className = "" }: {
  title?: React.ReactNode;
  desc?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`mt-9 first-of-type:mt-0 ${className}`}>
      {(title || action) && (
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 mb-3">
          <div className="min-w-0">
            {title && (
              <h3 className="m-0 text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</h3>
            )}
            {desc && <p className="m-0 mt-0.5 text-[13px] leading-[1.5] text-[var(--text-tertiary)]">{desc}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// A single setting: label + description left, control right. The ChatGPT row.
// Wide controls (swatch strips, button clusters) wrap below the label on
// phones instead of crushing it.
export function Row({ title, desc, children, className = "" }: {
  title?: React.ReactNode;
  desc?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-2.5 py-3.5 ${className}`}>
      <div className="flex-1 min-w-0 basis-[170px]">
        <div className="text-[13.5px] font-medium text-[var(--text-primary)]">{title}</div>
        {desc && <div className="text-[12.5px] leading-[1.5] text-[var(--text-tertiary)] mt-0.5">{desc}</div>}
      </div>
      <div className="shrink-0 flex items-center gap-2 min-w-0 max-w-full">{children}</div>
    </div>
  );
}

/* Rows in a white card inside a grey tray (outer radius 18 = card 14 + 4 inset).
   `header` sits on the tray above the card — column labels for a table-like list;
   `footer` on the tray's strip below it — the list's actions (Save, Upgrade),
   right-aligned. A list with neither is just the card in its tray. */
export function RowList({ children, header, footer, className = "" }: {
  children?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-[18px] bg-[var(--surface-2)] p-1 min-w-0 ${className}`}>
      {header && <div className="flex items-center gap-4 px-4 pt-1.5 pb-2 text-[12px] text-[var(--text-tertiary)] min-w-0">{header}</div>}
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift px-4 divide-y divide-[var(--hairline)] min-w-0">
        {children}
      </div>
      {footer && <div className="flex flex-wrap items-center justify-end gap-2 min-h-[44px] px-2.5 py-1.5">{footer}</div>}
    </div>
  );
}

export function Note({ children }: { children?: React.ReactNode }) {
  return <p className="m-0 mt-3 text-[12px] leading-[1.55] text-[var(--text-quaternary)]">{children}</p>;
}

export function FieldLabel({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <label className={cn("block text-[12.5px] font-medium text-[var(--text-secondary)] tracking-[-0.005em] mb-1.5", className)}>{children}</label>
  );
}

const fieldCls =
  "w-full rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[13.5px] text-[var(--text-primary)] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] transition-shadow";

/* `className` merges over the look (`cn`), for what a field adds: mono type, a danger ring. */
export function TextInput({ value, onChange, type = "text", className, ...rest }:
  Omit<React.ComponentPropsWithoutRef<"input">, "onChange"> & { onChange: (value: string) => void }
) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(fieldCls, "h-9 px-3", className)}
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, className, ...rest }:
  Omit<React.ComponentPropsWithoutRef<"textarea">, "onChange"> & { onChange: (value: string) => void }
) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(fieldCls, "px-3 py-2 leading-[1.55]", className)}
      {...rest}
    />
  );
}

export function PrimaryButton({ children, onClick, disabled = false, className = "", ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      onClick={onClick}
      disabled={disabled}
      className={`press inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-[10px] bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] shadow-button px-3.5 h-8 text-[13px] font-medium disabled:opacity-40 disabled:pointer-events-none ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, className = "", ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      onClick={onClick}
      className={`press inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift hover:bg-[var(--surface-sunk)] px-3 h-8 text-[13px] font-medium text-[var(--text-primary)] disabled:opacity-40 disabled:pointer-events-none ${className}`}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, onClick, className = "" }: {
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`press inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-[10px] px-3 h-8 text-[13px] font-medium text-[var(--danger)] ring-1 ring-[var(--danger-ring)] hover:bg-[var(--danger-bg)] ${className}`}
    >
      {children}
    </button>
  );
}

// Solid destructive action inside confirm dialogs.
export const confirmDangerCls =
  "press rounded-[10px] px-4 h-8 text-[13px] font-medium bg-[var(--danger)] text-white hover:bg-[var(--danger-hover)] disabled:opacity-40 disabled:pointer-events-none";
export const confirmDialogCls =
  "rounded-[16px] border border-[var(--hairline-strong)] bg-[var(--canvas)] shadow-dialog";

export function SelectInput({ value, onChange, options, disabled = false, compact = false, className = "", "aria-label": label }: {
  value?: string | number;
  onChange: (value: string) => void;
  options: Array<{ id: string | number; label: React.ReactNode }>;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
  /** What it picks, for a screen reader, when no visible label names it. */
  "aria-label"?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${compact ? "h-7 pl-2.5 pr-6 text-[12.5px] rounded-[8px]" : "h-9 pl-3 pr-7 text-[13.5px] rounded-[10px]"} appearance-none cursor-pointer bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift text-[var(--text-primary)] outline-none focus:ring-[var(--hairline-focus)] transition-shadow disabled:opacity-50 disabled:cursor-default`}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
      <ChevronDown
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] ${compact ? "right-2 w-3 h-3" : "right-2.5 w-3.5 h-3.5"}`}
        strokeWidth={1.75}
      />
    </div>
  );
}

export function StatusBadge({ children, tone = "neutral" }: {
  children?: React.ReactNode;
  tone?: "neutral" | "success" | "warn" | "danger" | "brand";
}) {
  const tones = {
    neutral: "bg-[var(--surface-2)] text-[var(--text-secondary)]",
    success: "bg-[var(--success-bg)] text-[var(--success)]",
    warn: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
    danger: "bg-[var(--danger-bg)] text-[var(--danger)]",
    brand: "bg-[var(--brand)] text-[var(--brand-foreground)]",
  };
  return (
    <span className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11.5px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}
